import { Injectable } from '@angular/core';
import { array } from '@common/util/array';
import { MetaDataVariables } from '@portal-core/project-files/models/meta-data-variables.model';
import { ProjectVariableSet } from '@portal-core/project-files/variables/models/project-variable-set.model';
import { VariablesApiService } from '@portal-core/project-files/variables/services/variables-api.service';
import { VariableFormatter } from '@portal-core/project-files/variables/util/variable-formatter';
import { VariableTextParser } from '@portal-core/project-files/variables/util/variable-text-parser';
import { isEmpty } from 'lodash';
import { Observable, combineLatest, map, of } from 'rxjs';

export interface LinkedText {
  path: string;
  text: string;
}

/**
 * Key - file path.
 * Value - array of linked system variables (long variable name is used).
 */
type LinkedSystemVariables = Dictionary<string[]>; //

@Injectable({
  providedIn: 'root'
})
export class VariablesService {
  constructor(
    private variablesApiService: VariablesApiService,
  ) { }

  getSystemVariableSets$(): Observable<ProjectVariableSet[]> {
    return this.variablesApiService.getSystemVariableSets$();
  }

  getSystemLinkedVariables(): string[] {
    return ['System.LinkedTitle', 'System.LinkedHeader', 'System.LinkedFile'];
  }


  processLinkedSystemVariables$(linkedTexts: LinkedText[], variablesMetaData$: Observable<MetaDataVariables>, projectId: number, commitId: string): Observable<LinkedText[]> {
    const linkedSystemVariables = this.collectLinkedSystemVariables(linkedTexts);

    const resolvedSystemVariables$: Observable<Dictionary<Dictionary<string>>> = isEmpty(linkedSystemVariables) ? of({})
      : this.variablesApiService.computeLinkedSystemVariables$(linkedSystemVariables, projectId, commitId);

    return combineLatest([resolvedSystemVariables$, variablesMetaData$]).pipe(
      map(([resolvedSystemVariablesData, variablesMetaData]) => {
        const parser = new VariableTextParser(variablesMetaData);
        return linkedTexts.map(item => {
          // at first process system variables
          let text = this.processSystemVariables(item.text, resolvedSystemVariablesData[item.path]);
          // then process project variables
          text = parser.processVariableText(text, true);
          return { path: item.path, text: text };
        });
      })
    );
  }

  private collectLinkedSystemVariables(linkedTexts: LinkedText[]): LinkedSystemVariables {
    const linkedSystemVariables: LinkedSystemVariables = {};
    linkedTexts.forEach(item => {
      if (!item.path)
        return;
      const systemVariables = VariableTextParser.lookupVariables(item.text, 'system');
      if (!array(systemVariables))
        return;
      const linkedVariables = linkedSystemVariables[item.path] ?? [];
      linkedSystemVariables[item.path] = [...linkedVariables, ...systemVariables];
    });
    return linkedSystemVariables;
  }

  private processSystemVariables(text: string, resolvedSystemVariables: Dictionary<string>): string {
    if (!resolvedSystemVariables)
      return text;
    const systemVariables = VariableTextParser.lookupVariables(text, 'system');
    systemVariables?.forEach(longName => {
      const variableValue = resolvedSystemVariables[longName] ?? VariableFormatter.formatUndefinedVariable(longName);
      text = text.replaceAll(VariableFormatter.formatTextVariable(longName), variableValue);
    });
    return text;
  }

}
